export * from "./types";
export { defaultPresets } from "./presets";
import { addAddressPadding, constants, RpcProvider, } from "starknet";
import { connectToChild, } from "@cartridge/penpal";
import DeviceAccount from "./device";
import { ResponseCodes, } from "./types";
import { createModal } from "./modal";
import { defaultPresets } from "./presets";
import { NotReadyToConnect } from "./errors";
// @dev override url to local sequencer for local dev
// http://localhost:8000/x/starknet/mainnet
export const providers = {
    [constants.StarknetChainId.SN_MAIN]: new RpcProvider({
        nodeUrl: "https://api.cartridge.gg/x/starknet/mainnet",
    }),
    [constants.StarknetChainId.SN_SEPOLIA]: new RpcProvider({
        nodeUrl: "https://api.cartridge.gg/x/starknet/sepolia",
    }),
};
class Controller {
    // private starterPackId?: string;
    constructor(policies, options) {
        this.policies = [];
        this.url = "https://x.cartridge.gg";
        this.chainId = constants.StarknetChainId.SN_SEPOLIA;
        if (policies) {
            this.policies = policies.map((policy) => {
                return {
                    ...policy,
                    target: addAddressPadding(policy.target),
                };
            });
        }
        if (options?.chainId) {
            this.chainId = options.chainId;
        }
        if (options?.url) {
            this.url = options.url;
        }
        this.setTheme(options?.theme, options?.config?.presets);
        if (options?.colorMode) {
            this.setColorMode(options.colorMode);
        }
        if (typeof document === "undefined") {
            return;
        }
        this.modal = createModal(this.url, () => {
            this.keychain?.reset();
        });
        if (document.readyState === "complete" ||
            document.readyState === "interactive") {
            document.body.appendChild(this.modal.element);
        }
        else {
            document.addEventListener("DOMContentLoaded", () => {
                document.body.appendChild(this.modal.element);
            });
        }
        this.connection = connectToChild({
            iframe: this.modal.element.children[0],
        });
        this.connection.promise
            .then((keychain) => (this.keychain = keychain))
            .then(() => this.probe());
    }
    get account() {
        if (!this.accounts) {
            return;
        }
        return this.accounts[this.chainId];
    }
    setTheme(id = "cartridge", presets = defaultPresets) {
        const theme = presets[id] ?? defaultPresets.cartridge;
        const url = new URL(this.url);
        url.searchParams.set("theme", encodeURIComponent(JSON.stringify(theme)));
        this.url = url.toString();
    }
    setColorMode(colorMode) {
        const url = new URL(this.url);
        url.searchParams.set("colorMode", colorMode);
        this.url = url.toString();
    }
    ready() {
        return (this.connection?.promise
            .then(() => this.probe())
            .then((res) => !!res, () => false) ?? Promise.resolve(false));
    }
    async probe() {
        if (!this.keychain || !this.modal) {
            console.error(new NotReadyToConnect().message);
            return null;
        }
        try {
            const res = await this.keychain.probe();
            if (res.code !== ResponseCodes.SUCCESS) {
                return;
            }
            const { address } = res;
            this.accounts = {
                [constants.StarknetChainId.SN_MAIN]: new DeviceAccount(providers[constants.StarknetChainId.SN_MAIN], address, this.keychain, this.modal), // Note: workaround for execute type mismatch error
                [constants.StarknetChainId.SN_SEPOLIA]: new DeviceAccount(providers[constants.StarknetChainId.SN_SEPOLIA], address, this.keychain, this.modal),
            };
        }
        catch (e) {
            console.error(e);
            return;
        }
        return !!this.accounts?.[this.chainId];
    }
    async switchChain(chainId) {
        if (this.chainId === chainId) {
            return;
        }
        this.chainId = chainId;
    }
    async login(address, credentialId, options) {
        if (!this.keychain) {
            console.error(new NotReadyToConnect().message);
            return null;
        }
        return this.keychain.login(address, credentialId, options);
    }
    async issueStarterPack(id) {
        if (!this.keychain || !this.modal) {
            const err = new NotReadyToConnect();
            console.error(err.message);
            return Promise.reject(err.message);
        }
        this.modal.open();
        try {
            if (!this.account) {
                let response = await this.keychain.connect(this.policies, undefined, this.chainId);
                if (response.code !== ResponseCodes.SUCCESS) {
                    throw new Error(response.message);
                }
            }
            return await this.keychain.issueStarterPack(id);
        }
        catch (e) {
            console.log(e);
            return Promise.reject(e);
        }
        finally {
            this.modal.close();
        }
    }
    async showQuests(gameId) {
        if (!this.keychain || !this.modal) {
            console.error(new NotReadyToConnect().message);
            return;
        }
        this.modal.open();
        try {
            return await this.keychain.showQuests(gameId);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            this.modal.close();
        }
    }
    async connect() {
        if (this.accounts) {
            return this.accounts[this.chainId];
        }
        if (!this.keychain || !this.modal) {
            console.error(new NotReadyToConnect().message);
            return;
        }
        if (!!document.hasStorageAccess) {
            const ok = await document.hasStorageAccess();
            if (!ok) {
                await document.requestStorageAccess();
            }
        }
        this.modal.open();
        try {
            let response = await this.keychain.connect(this.policies, undefined, this.chainId);
            if (response.code !== ResponseCodes.SUCCESS) {
                throw new Error(response.message);
            }
            response = response;
            this.accounts = {
                [constants.StarknetChainId.SN_MAIN]: new DeviceAccount(providers[constants.StarknetChainId.SN_MAIN], response.address, this.keychain, this.modal),
                [constants.StarknetChainId.SN_SEPOLIA]: new DeviceAccount(providers[constants.StarknetChainId.SN_SEPOLIA], response.address, this.keychain, this.modal),
            };
            return this.accounts[this.chainId];
        }
        catch (e) {
            console.log(e);
        }
        finally {
            this.modal.close();
        }
    }
    async disconnect() {
        if (!this.keychain) {
            console.error(new NotReadyToConnect().message);
            return;
        }
        if (!!document.hasStorageAccess) {
            const ok = await document.hasStorageAccess();
            if (!ok) {
                await document.requestStorageAccess();
            }
        }
        this.accounts = undefined;
        return this.keychain.disconnect();
    }
    revoke(origin, _policy) {
        if (!this.keychain) {
            console.error(new NotReadyToConnect().message);
            return null;
        }
        return this.keychain.revoke(origin);
    }
    async approvals(origin) {
        if (!this.keychain) {
            console.error(new NotReadyToConnect().message);
            return;
        }
        return this.keychain.approvals(origin);
    }
    username() {
        if (!this.keychain) {
            console.error(new NotReadyToConnect().message);
            return;
        }
        return this.keychain.username();
    }
}
export * from "./types";
export * from "./errors";
export { computeAddress, split, verifyMessageHash } from "./utils";
export { injectController } from "./inject";
export default Controller;
//# sourceMappingURL=index.js.map